// client/src/components/pages/LexiStar/NotificationRules/TemplatePreview.jsx
//
// Live preview of a message template per language (FR-NTF-005; LS-09 §5.16),
// rendered by the server (POST /message-templates:preview — the same
// Handlebars engine and helpers the evaluator uses). Shows the quick-reply
// buttons, the SMS segment count, and a warning when WhatsApp would not send
// the template (only provider-approved templates go out on WhatsApp).

import React, { useEffect, useState } from 'react';
import SegmentedControl from '../../../common/SegmentedControl';
import { previewTemplate, renderedOf } from './notificationsApi';
import { channelName } from './ruleLogic';

const LANGS = [
  { value: 'en', label: 'English' },
  { value: 'af', label: 'Afrikaans' },
];

export default function TemplatePreview({
  templateKey,
  channel = 'in_app',
  vars,
  body,
  buttons,
  debounceMs = 300,
  className = '',
}) {
  const [lang, setLang] = useState('en');
  const [state, setState] = useState({ loading: false, error: null, rendered: null });
  const varsKey = JSON.stringify(typeof vars === 'function' ? vars(lang) : vars || {});
  const buttonsKey = JSON.stringify(buttons || null);

  useEffect(() => {
    if (!templateKey) {
      setState({ loading: false, error: null, rendered: null });
      return undefined;
    }
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    const timer = setTimeout(() => {
      previewTemplate({
        key: templateKey,
        channel,
        language: lang,
        vars: JSON.parse(varsKey),
        ...(body !== undefined && body !== null ? { body } : {}),
        ...(buttonsKey !== 'null' ? { buttons: JSON.parse(buttonsKey) } : {}),
      })
        .then(
          (res) => alive && setState({ loading: false, error: null, rendered: renderedOf(res) })
        )
        .catch((err) => {
          if (!alive) return;
          const status = err.response?.status;
          setState({
            loading: false,
            rendered: null,
            error:
              status === 404
                ? `No ${channelName(channel)} template "${templateKey}" in ${lang === 'af' ? 'Afrikaans' : 'English'}.`
                : err.response?.data?.error || 'The preview could not be rendered.',
          });
        });
    }, debounceMs);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [templateKey, channel, lang, varsKey, body, buttonsKey, debounceMs]);

  const r = state.rendered;
  const seg = r?.segments;
  const waBlocked =
    channel === 'whatsapp' &&
    r?.providerStatus &&
    !['approved', 'n/a'].includes(String(r.providerStatus));

  return (
    <div className={`space-y-2 ${className}`} aria-label="Message preview" role="region">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-slate-500">
          Preview · {channelName(channel)}
          {state.loading ? ' · rendering…' : ''}
        </span>
        <SegmentedControl
          options={LANGS}
          value={lang}
          onChange={setLang}
          ariaLabel="Preview language"
        />
      </div>
      {state.error ? (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {state.error}
        </p>
      ) : r ? (
        <div className="rounded-xl bg-slate-100 p-3">
          <div className="max-w-md rounded-2xl rounded-tl-sm bg-surface shadow-sm px-3 py-2">
            <p className="whitespace-pre-wrap text-sm text-slate-900" data-testid="preview-body">
              {r.body || <span className="italic text-slate-400">(empty)</span>}
            </p>
          </div>
          {r.buttons.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Reply buttons">
              {r.buttons.map((b) => (
                <span
                  key={b.id ?? b.label}
                  className="px-3 py-1 rounded-full bg-surface text-xs font-semibold text-primary-700 shadow-sm ring-1 ring-slate-200"
                >
                  {b.label}
                </span>
              ))}
            </div>
          )}
          {channel === 'sms' && seg && (
            <p
              className={`mt-2 text-xs ${Number(seg.segments) > 1 ? 'text-amber-800' : 'text-slate-500'}`}
            >
              {seg.characters ?? '?'} characters · {seg.segments} SMS segment
              {Number(seg.segments) === 1 ? '' : 's'}
              {seg.encoding ? ` (${String(seg.encoding).toUpperCase()})` : ''}
            </p>
          )}
          {r.languageFallback && lang !== 'en' && (
            <p className="mt-2 text-xs text-amber-800">
              There is no Afrikaans version of this template — the English one is sent.
            </p>
          )}
          {waBlocked && (
            <p className="mt-2 text-xs text-amber-800">
              WhatsApp template status is “{r.providerStatus}” — WhatsApp only sends approved
              templates, so delivery falls back to the next channel until it is approved.
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-400 italic">
          {templateKey ? 'Rendering the preview…' : 'Choose a template to preview it.'}
        </p>
      )}
    </div>
  );
}
